// De vragen van de DierMatch-matchwizard (epic 12, story 12-2) — als gegevens, niet als JSX.
//
// Waarom een model en geen reeks schermen: de wizard moet per diersoort andere vragen stellen, en
// een verborgen vraag mag de uitkomst niet beïnvloeden. Dat zijn twee regels over de hele vragenset
// tegelijk, en die zijn alleen te garanderen als de set één ding is dat je kunt aflopen en testen.
// Het scherm leest dit model; het bedenkt niets zelf.
//
// Elke vraag hier draagt een `kenmerk`: de sleutel uit de backoffice-catalogus waar het antwoord
// straks (story 12-3/12-4) tegenaan gescoord wordt. Dat veld is de reden dat een vraag bestaat.
// Een vraag zonder kenmerk zou een vraag zijn waar niets mee gebeurt, en die hoort hier niet —
// zie de notitie "Wat we bewust niet vragen" onderaan.

/** De vijf vraagstappen plus het resultaat; de volgorde is de volgorde van de stappenbalk. */
export const STAPPEN = [
  {
    id: "situatie",
    label: "Jouw situatie",
    kop: "Jouw situatie",
    onder: "Vertel ons wat over jezelf en je huishouden.",
    waarom: "Wie er in huis wonen bepaalt een groot deel van de match. Of een dier bij kinderen "
      + "kan, legt het asiel per dier vast — met een leeftijd erbij als dat nodig is.\n\n"
      + "We vergelijken jouw antwoord met dát antwoord. Staat het er niet, dan noemen we het "
      + "onzeker in plaats van passend.",
  },
  {
    id: "wonen",
    label: "Wonen",
    kop: "Woonsituatie",
    onder: "Waar en hoe woon je?",
    waarom: "De woonomgeving heeft invloed op welke honden bij je passen. Een grote hond in een "
      + "appartement zonder tuin heeft het zwaarder, en een waakse hond valt in de stad meer op "
      + "bij de buren.\n\nHet asiel vult schofthoogte en waaksheid per hond in; daar vergelijken "
      + "we dit mee.",
  },
  {
    id: "dieren",
    label: "Dieren",
    kop: "Andere dieren",
    onder: "Heb je al huisdieren?",
    waarom: "Een nieuw dier komt binnen bij de dieren die er al wonen. Het asiel legt per dier "
      + "vast of het met honden of katten overweg kan.\n\nHet gaat twee kanten op: ook of jóúw "
      + "dier sociaal is telt mee. Een kattenvriendelijke hond is nog geen match als je eigen kat "
      + "geen ander dier verdraagt.",
  },
  {
    id: "leefstijl",
    label: "Leefstijl",
    kop: "Leefstijl",
    onder: "Hoeveel tijd en aandacht kun je geven?",
    waarom: "De hoeveelheid tijd en beweging die je kunt bieden is belangrijk voor het welzijn "
      + "van het dier.\n\nZo zorgen we voor een match die past bij jouw leven én bij de behoeften "
      + "van het dier — in plaats van bij het dier dat toevallig het liefst op de foto staat.",
  },
  {
    id: "voorkeuren",
    label: "Voorkeuren",
    kop: "Voorkeuren",
    onder: "Wat voor dier past bij jou?",
    waarom: "Hier bepaal jij wat écht moet kloppen. Een harde eis sluit dieren uit — ook dieren "
      + "waarvan het asiel het antwoord nog niet heeft ingevuld, want onbekend is geen ja.\n\n"
      + "Wat je niet hard maakt verdwijnt niet: het weegt mee in de volgorde van je top 10.",
  },
  {
    id: "resultaat",
    label: "Resultaten",
    kop: "Klaar om te kijken",
    onder: "Dit is wat we voor je gaan zoeken.",
    waarom: "",
  },
] as const;

export type StapId = (typeof STAPPEN)[number]["id"];

/** De vraagstappen zonder het resultaat — waar de voortgang over gaat. */
export const VRAAGSTAPPEN = STAPPEN.filter((s) => s.id !== "resultaat");

export type Vorm = "enkel" | "meervoud" | "tekst";

export interface Optie {
  waarde: string;
  label: string;
  /** Kleine regel onder het label; mag weg. */
  hint?: string;
  /** Naam voor `Icoon` in het scherm; zonder icoon wordt de kaart een tekstkaart. */
  icoon?: string;
  /**
   * Wist bij aanklikken alle andere keuzes en kan niet samen met iets anders aan.
   *
   * <p>"Nee, geen kinderen" naast "Ja, 0–6 jaar" is geen antwoord maar een tegenspraak. In plaats
   * van dat achteraf af te keuren, maakt de keuze zichzelf onmogelijk.
   */
  exclusief?: boolean;
  /**
   * Alleen tonen als dit waar is — net als bij een vraag, maar dan per keuze.
   *
   * <p>Nodig omdat niet alleen vragen maar ook losse antwoorden van eerdere antwoorden afhangen.
   * "Moet goed met katten kunnen" is geen zinnige eis voor iemand zonder kat, en "Nee, geen
   * kinderen" is geen zinnig antwoord voor iemand die net zei dat hij met zijn gezin woont.
   */
  toonAls?: (antwoorden: Antwoorden) => boolean;
}

export interface Vraag {
  sleutel: string;
  stap: StapId;
  /**
   * De vraag zoals hij op het scherm staat.
   *
   * <p>Mag een functie zijn: dezelfde onderliggende vraag luidt anders afhankelijk van wat er al
   * beantwoord is. "Welke leeftijden hebben de kinderen?" bij een gezin, "Komen er kinderen over de
   * vloer?" bij iemand die alleen woont — één veld, twee formuleringen, en geen van beide een
   * tegenspraak met het antwoord ervoor.
   */
  titel: string | ((antwoorden: Antwoorden) => string);
  vorm: Vorm;
  opties?: Optie[];
  /** Zonder antwoord kan de bezoeker niet verder. Spaarzaam gebruiken. */
  verplicht?: boolean;
  /** Plaatshouder voor een tekstvraag. */
  plaatshouder?: string;
  /**
   * Het catalogus-kenmerk (of het dierveld) waar dit antwoord tegenaan gescoord wordt.
   *
   * <p>Documentatie én controle: `vragenZonderDoel()` valt om zodra iemand een vraag toevoegt die
   * nergens op uitkomt.
   */
  kenmerk: string;
  /** Alleen tonen als dit waar is. Afwezig = altijd tonen. */
  toonAls?: (antwoorden: Antwoorden) => boolean;
}

/** Wat de bezoeker heeft ingevuld: per vraagsleutel nul of meer waarden. */
export type Antwoorden = Record<string, string[]>;

// ── hulpjes voor `toonAls` ──────────────────────────────────────────────────────────────────────

const heeft = (antwoorden: Antwoorden, sleutel: string, waarde: string): boolean =>
  (antwoorden[sleutel] ?? []).includes(waarde);

/** Zoekt de bezoeker (ook) naar een hond? "Maakt niet uit" telt mee: dan kan het een hond worden. */
export const zoektHond = (a: Antwoorden): boolean =>
  heeft(a, "soort", "honden") || heeft(a, "soort", "maakt-niet-uit");

export const zoektKat = (a: Antwoorden): boolean =>
  heeft(a, "soort", "katten") || heeft(a, "soort", "maakt-niet-uit");

/** Woont er een gezin? Dan staat vast dát er kinderen zijn, en is alleen hun leeftijd nog open. */
export const woontMetGezin = (a: Antwoorden): boolean => heeft(a, "huishouden", "gezin");

/** Zijn er kinderen in beeld — in huis of over de vloer? */
export const heeftKinderen = (a: Antwoorden): boolean =>
  (a.kinderen ?? []).some((w) => w !== "nee");

export const heeftHond = (a: Antwoorden): boolean => heeft(a, "huisdieren", "hond");

export const heeftKat = (a: Antwoorden): boolean => heeft(a, "huisdieren", "kat");

// ── de vragen ───────────────────────────────────────────────────────────────────────────────────

export const VRAGEN: Vraag[] = [
  // ── 1. Jouw situatie ──
  {
    // Vóór alles, want hierop hangt welke vervolgvragen er überhaupt zijn. In de mockup staat de
    // diersoort bij "Voorkeuren" (stap 5), maar het bouwplan §4 zegt expliciet: eerst uitvragen,
    // dan pas de soortspecifieke vragen. Een wizard die op stap 2 al naar wandelduur vraagt en pas
    // op stap 5 hoort dat je een konijn zoekt, stelt vier stappen lang de verkeerde vragen.
    sleutel: "soort",
    stap: "situatie",
    titel: "Waar ben je naar op zoek?",
    vorm: "meervoud",
    verplicht: true,
    kenmerk: "soortGroep",
    opties: [
      { waarde: "honden", label: "Een hond", icoon: "hond" },
      { waarde: "katten", label: "Een kat", icoon: "kat" },
      { waarde: "konijnen-en-knagers", label: "Konijn of knaagdier", icoon: "konijn" },
      { waarde: "vogels", label: "Een vogel", icoon: "vogel" },
      { waarde: "maakt-niet-uit", label: "Maakt niet uit", icoon: "hart", exclusief: true },
    ],
  },
  {
    // Verplicht, want de kindervraag hangt eraan: zonder dit antwoord weten we niet of "Nee, geen
    // kinderen" überhaupt een mogelijk antwoord is.
    sleutel: "huishouden",
    stap: "situatie",
    titel: "Met wie woon je?",
    vorm: "enkel",
    verplicht: true,
    kenmerk: "huishouden",
    opties: [
      { waarde: "alleen", label: "Alleen", icoon: "persoon" },
      { waarde: "partner", label: "Met partner", icoon: "stel" },
      { waarde: "gezin", label: "Met gezin", icoon: "gezin" },
      { waarde: "huisgenoten", label: "Met huisgenoten", icoon: "mensen" },
    ],
  },
  {
    // Eén veld, twee formuleringen. Wie "met gezin" antwoordde krijgt niet nóg eens de vraag óf er
    // kinderen zijn — dat zei hij net — maar alleen welke leeftijden. Wie alleen of met een partner
    // woont krijgt de vraag over kinderen die langskomen: voor een hond die niet tegen jonge
    // kinderen kan is een kleinkind dat elke woensdag komt net zo goed een gegeven.
    sleutel: "kinderen",
    stap: "situatie",
    titel: (a) =>
      woontMetGezin(a)
        ? "Welke leeftijden hebben de kinderen?"
        : "Komen er kinderen over de vloer?",
    vorm: "meervoud",
    verplicht: true,
    kenmerk: "KAN_BIJ_KINDEREN",
    toonAls: (a) => (a.huishouden?.length ?? 0) > 0,
    opties: [
      // "Nee" verdwijnt bij een gezin: dat zou de vorige vraag tegenspreken.
      { waarde: "nee", label: "Nee", exclusief: true, toonAls: (a) => !woontMetGezin(a) },
      { waarde: "0-6", label: "0 – 6 jaar" },
      { waarde: "7-12", label: "7 – 12 jaar" },
      { waarde: "13plus", label: "13 jaar en ouder" },
    ],
  },

  // ── 2. Wonen ──
  // Alle drie alleen bij honden. Niet uit luiheid: woning en tuin worden gescoord tegen
  // SCHOFTHOOGTE en de omgeving tegen WAAKSHEID, en die twee kenmerken vult een asiel alleen bij
  // honden in (op acceptatie: 81 van de 105 honden, nul katten). Wie een kat zoekt zou hier dus
  // drie vragen beantwoorden waar daarna niets mee gebeurt. De stap valt dan in zijn geheel weg.
  //
  // Voor katten hoort hier een binnen-/buitenvraag (bouwplan S03). Die kan pas als de
  // KenmerkCatalogus van de backoffice er een sleutel voor heeft — zonder antwoord aan de dierkant
  // valt er niets tegenaan te scoren.
  {
    sleutel: "woning",
    stap: "wonen",
    titel: "In wat voor woning woon je?",
    vorm: "enkel",
    kenmerk: "SCHOFTHOOGTE",
    toonAls: zoektHond,
    // De indeling van funda, want die kent iedereen: appartement, en daaronder de woonhuizen van
    // dicht op elkaar naar vrijstaand. "Rijtjeshuis" dekte tussen- en hoekwoning samen en liet
    // 2-onder-1-kap helemaal weg — het meest voorkomende type dat er niet bij stond.
    opties: [
      { waarde: "appartement", label: "Appartement", icoon: "appartement" },
      { waarde: "tussenwoning", label: "Tussenwoning", icoon: "tussenwoning" },
      { waarde: "hoekwoning", label: "Hoekwoning", icoon: "hoekwoning" },
      { waarde: "twee-onder-een-kap", label: "2-onder-1-kap", icoon: "tweeonderkap" },
      { waarde: "vrijstaand", label: "Vrijstaand", icoon: "vrijstaand" },
      { waarde: "woonboerderij", label: "Woonboerderij", icoon: "boerderij" },
    ],
  },
  {
    sleutel: "tuin",
    stap: "wonen",
    titel: "Heb je buitenruimte?",
    vorm: "enkel",
    kenmerk: "SCHOFTHOOGTE",
    toonAls: zoektHond,
    opties: [
      { waarde: "geen", label: "Geen tuin", icoon: "binnen" },
      { waarde: "klein", label: "Kleine tuin of balkon", icoon: "kleinetuin" },
      { waarde: "groot", label: "Grote tuin", icoon: "tuin" },
    ],
  },
  {
    sleutel: "omgeving",
    stap: "wonen",
    titel: "Hoe is je omgeving?",
    vorm: "enkel",
    kenmerk: "WAAKSHEID",
    toonAls: zoektHond,
    opties: [
      { waarde: "stad", label: "Stad", hint: "Druk, veel prikkels" },
      { waarde: "dorp", label: "Dorp of wijk", hint: "Rustiger, wel buren dichtbij" },
      { waarde: "buiten", label: "Buitengebied", hint: "Ruim en rustig" },
    ],
  },

  // ── 3. Andere dieren ──
  {
    sleutel: "huisdieren",
    stap: "dieren",
    titel: "Welke dieren wonen er nu bij je?",
    vorm: "meervoud",
    kenmerk: "KAN_BIJ_HONDEN",
    opties: [
      { waarde: "geen", label: "Geen huisdieren", icoon: "nee", exclusief: true },
      { waarde: "hond", label: "Hond(en)", icoon: "hond" },
      { waarde: "kat", label: "Kat(ten)", icoon: "kat" },
      { waarde: "knaagdier", label: "Konijn of knaagdier", icoon: "knaagdier" },
      { waarde: "vogel", label: "Vogel(s)", icoon: "vogel" },
    ],
  },
  {
    // Alleen zinnig als er een hond woont — anders is het een vraag over een dier dat er niet is.
    sleutel: "hondSociaal",
    stap: "dieren",
    titel: "Is je hond sociaal met andere dieren?",
    vorm: "enkel",
    kenmerk: "KAN_BIJ_HONDEN",
    toonAls: (a) => heeft(a, "huisdieren", "hond"),
    opties: [
      { waarde: "ja", label: "Ja" },
      { waarde: "nee", label: "Nee" },
      { waarde: "onbekend", label: "Weet ik niet" },
    ],
  },
  {
    sleutel: "katSociaal",
    stap: "dieren",
    titel: "Is je kat sociaal met andere dieren?",
    vorm: "enkel",
    kenmerk: "KAN_BIJ_KATTEN",
    toonAls: (a) => heeft(a, "huisdieren", "kat"),
    opties: [
      { waarde: "ja", label: "Ja" },
      { waarde: "nee", label: "Nee" },
      { waarde: "onbekend", label: "Weet ik niet" },
    ],
  },
  {
    // Veel dieren mogen alleen samen weg — konijnen vrijwel altijd. Zonder deze vraag valt een
    // koppel stilzwijgend af bij iedereen, en dat zijn precies de dieren die het langst wachten.
    sleutel: "duo",
    stap: "dieren",
    titel: "Sta je open voor twee dieren samen?",
    vorm: "enkel",
    kenmerk: "MOET_BIJ_SOORTGENOTEN",
    opties: [
      { waarde: "ja", label: "Ja, een koppel mag", icoon: "hart" },
      { waarde: "misschien", label: "Misschien", hint: "Vertel me er meer over" },
      { waarde: "nee", label: "Liever één dier" },
    ],
  },

  // ── 4. Tijd en leefstijl ──
  {
    sleutel: "afwezigheid",
    stap: "leefstijl",
    titel: "Hoe lang is het dier doordeweeks alleen?",
    vorm: "enkel",
    kenmerk: "KAN_ALLEEN_THUISBLIJVEN",
    opties: [
      { waarde: "nauwelijks", label: "Bijna nooit alleen", icoon: "binnen" },
      { waarde: "tot4", label: "Tot 4 uur" },
      { waarde: "4tot8", label: "4 tot 8 uur" },
      { waarde: "meer8", label: "Meer dan 8 uur" },
    ],
  },
  {
    sleutel: "verzorging",
    stap: "leefstijl",
    titel: "Hoeveel tijd heb je voor verzorging?",
    vorm: "enkel",
    kenmerk: "VACHTVERZORGING",
    opties: [
      { waarde: "weinig", label: "Weinig", hint: "Liefst weinig onderhoud" },
      { waarde: "gemiddeld", label: "Gemiddeld" },
      { waarde: "veel", label: "Veel", hint: "Borstelen vind ik leuk" },
    ],
  },
  {
    sleutel: "activiteit",
    stap: "leefstijl",
    titel: "Hoe actief ben je zelf?",
    vorm: "enkel",
    kenmerk: "AAN_DE_LIJN",
    toonAls: zoektHond,
    opties: [
      { waarde: "rustig", label: "Rustig" },
      { waarde: "gemiddeld", label: "Gemiddeld" },
      { waarde: "actief", label: "Actief" },
    ],
  },
  {
    sleutel: "wandelen",
    stap: "leefstijl",
    titel: "Hoe vaak ga je graag naar buiten?",
    vorm: "enkel",
    kenmerk: "KAN_LOSLOPEN",
    toonAls: zoektHond,
    opties: [
      { waarde: "zelden", label: "Zelden" },
      { waarde: "1-2", label: "1 – 2 keer per dag" },
      { waarde: "3-4", label: "3 – 4 keer per dag" },
      { waarde: "veel", label: "Heel graag", hint: "Veel wandelen" },
    ],
  },

  // ── 5. Voorkeuren ──
  {
    sleutel: "leeftijdsvoorkeur",
    stap: "voorkeuren",
    titel: "Welke leeftijd heeft je voorkeur?",
    vorm: "meervoud",
    kenmerk: "leeftijdGroep",
    opties: [
      { waarde: "jonger-dan-1", label: "Jonger dan 1 jaar" },
      { waarde: "1-tot-3", label: "1 tot en met 3 jaar" },
      { waarde: "4-tot-8", label: "4 tot en met 8 jaar" },
      { waarde: "9-en-ouder", label: "9 jaar en ouder" },
      { waarde: "maakt-niet-uit", label: "Maakt niet uit", exclusief: true },
    ],
  },
  {
    // Dit is het hard/zacht-onderscheid uit besluit A3, als één vraag. Wat hier aan staat sluit
    // dieren uit — inclusief dieren waarvan het asiel het antwoord niet heeft ingevuld. Wat uit
    // staat weegt alleen mee in de rangschikking. De bezoeker beslist dat, niet de engine.
    sleutel: "harde",
    stap: "voorkeuren",
    titel: "Wat moet er echt kloppen?",
    vorm: "meervoud",
    kenmerk: "harde-eisen",
    opties: [
      // Elke eis hangt af van wat er eerder is gezegd. "Moet goed met katten kunnen" aanbieden aan
      // iemand zonder kat levert een eis op die dieren uitsluit zonder dat er iets op het spel staat.
      {
        waarde: "kinderen",
        label: "Moet goed met kinderen kunnen",
        hint: "Dieren zonder bevestigd antwoord vallen af",
        toonAls: heeftKinderen,
      },
      {
        waarde: "honden",
        label: "Moet goed met honden kunnen",
        hint: "Dieren zonder bevestigd antwoord vallen af",
        toonAls: heeftHond,
      },
      {
        waarde: "katten",
        label: "Moet goed met katten kunnen",
        hint: "Dieren zonder bevestigd antwoord vallen af",
        toonAls: heeftKat,
      },
      { waarde: "geen-extra-zorg", label: "Geen extra zorg nodig" },
      { waarde: "niet-groot", label: "Niet te groot", toonAls: zoektHond },
      { waarde: "geen", label: "Geen harde eisen", exclusief: true },
    ],
  },
  {
    sleutel: "plaats",
    stap: "voorkeuren",
    titel: "Waar zoek je?",
    vorm: "tekst",
    plaatshouder: "Plaats of postcode",
    kenmerk: "afstand",
  },
  {
    sleutel: "straal",
    stap: "voorkeuren",
    titel: "Hoe ver wil je reizen?",
    vorm: "enkel",
    kenmerk: "afstand",
    toonAls: (a) => (a.plaats?.[0] ?? "").trim().length > 0,
    opties: [
      { waarde: "10", label: "10 km" },
      { waarde: "25", label: "25 km" },
      { waarde: "50", label: "50 km" },
      { waarde: "", label: "Heel Nederland", exclusief: true },
    ],
  },
];

// ── afgeleiden ──────────────────────────────────────────────────────────────────────────────────

/** De vragen van één stap die bij deze antwoorden zichtbaar zijn. */
export function vragenVoor(stap: StapId, antwoorden: Antwoorden): Vraag[] {
  return VRAGEN.filter((v) => v.stap === stap && (!v.toonAls || v.toonAls(antwoorden)));
}

/** De keuzes van één vraag die bij deze antwoorden zichtbaar zijn. */
export function optiesVoor(vraag: Vraag, antwoorden: Antwoorden): Optie[] {
  return (vraag.opties ?? []).filter((o) => !o.toonAls || o.toonAls(antwoorden));
}

/** De vraag zoals hij nu luidt; een vraag mag van eerdere antwoorden afhangen. */
export function titelVan(vraag: Vraag, antwoorden: Antwoorden): string {
  return typeof vraag.titel === "function" ? vraag.titel(antwoorden) : vraag.titel;
}

/**
 * Een (sleutel, waarde)-paar als leesbare regel, voor de chips uit de vrije wens.
 *
 * <p>Het label komt hier vandaan en niet van de server: de AI levert alleen sleutel en waarde, en de
 * woorden op het scherm horen uit de vragenlijst te komen. Zo kan een voorstel nooit een zin tonen
 * die niet in deze lijst staat.
 *
 * <p>`null` voor een paar dat hier niet bestaat — dan is er niets te laten zien.
 */
export function chipLabel(
  sleutel: string,
  waarde: string,
  antwoorden: Antwoorden,
): { vraag: Vraag; label: string } | null {
  const vraag = VRAGEN.find((v) => v.sleutel === sleutel);
  if (!vraag) {
    return null;
  }
  // Tegen de ZICHTBARE keuzes: een voorstel als "moet goed met katten kunnen" hoort te verdwijnen
  // bij iemand zonder kat, ook als het model het keurig uit de tekst haalde.
  const optie = optiesVoor(vraag, antwoorden).find((o) => o.waarde === waarde);
  return optie ? { vraag, label: optie.label } : null;
}

/**
 * De stappen die bij deze antwoorden iets te vragen hebben, plus het resultaat.
 *
 * <p>Een stap zonder zichtbare vragen bestaat niet voor deze bezoeker. Wie een kat zoekt krijgt
 * geen lege stap "Wonen" te zien met een Volgende-knop eronder: die stap verdwijnt uit de balk en
 * uit de route. Dat is wat "dynamisch per diersoort" betekent als je het consequent doortrekt.
 */
export function zichtbareStappen(antwoorden: Antwoorden): StapId[] {
  const stappen = VRAAGSTAPPEN
    .filter((s) => vragenVoor(s.id, antwoorden).length > 0)
    .map((s) => s.id as StapId);
  return [...stappen, "resultaat"];
}

/** Of alle verplichte, zichtbare vragen van deze stap beantwoord zijn. */
export function stapCompleet(stap: StapId, antwoorden: Antwoorden): boolean {
  return vragenVoor(stap, antwoorden)
    .filter((v) => v.verplicht)
    .every((v) => (antwoorden[v.sleutel] ?? []).some((w) => w.trim().length > 0));
}

/**
 * De antwoorden ontdaan van alles wat niet (meer) zichtbaar is.
 *
 * <p>Het acceptatiecriterium "verborgen vragen hebben geen invloed op scoring" is hiermee geen
 * belofte over het scherm maar een eigenschap van wat de engine binnenkrijgt. Nodig omdat een
 * bezoeker terug kan lopen: wie eerst "hond" kiest, wandelduur invult en daarna naar "kat"
 * wisselt, heeft een antwoord staan op een vraag die niet meer bestaat.
 */
export function zichtbareAntwoorden(antwoorden: Antwoorden): Antwoorden {
  const zichtbaar: Antwoorden = {};
  for (const vraag of VRAGEN) {
    if (vraag.toonAls && !vraag.toonAls(antwoorden)) {
      continue;
    }
    const waarde = antwoorden[vraag.sleutel];
    if (!waarde || waarde.length === 0) {
      continue;
    }
    if (vraag.vorm === "tekst") {
      zichtbaar[vraag.sleutel] = waarde;
      continue;
    }
    // Ook losse keuzes kunnen onzichtbaar worden: wie "Moet goed met katten kunnen" aanvinkte en
    // daarna zijn kat weghaalde, heeft een eis staan die nergens meer op slaat.
    const toegestaan = new Set(optiesVoor(vraag, antwoorden).map((o) => o.waarde));
    const over = waarde.filter((w) => toegestaan.has(w));
    if (over.length > 0) {
      zichtbaar[vraag.sleutel] = over;
    }
  }
  return zichtbaar;
}

/**
 * Vragen die nergens op uitkomen — hoort altijd leeg te zijn.
 *
 * <p>Bestaat als test-haakje, niet voor het scherm: een vraag toevoegen zonder te bedenken waar het
 * antwoord tegenaan gescoord wordt is precies de fout die een wizard lang maakt en nutteloos.
 */
export function vragenZonderDoel(): Vraag[] {
  return VRAGEN.filter((v) => !v.kenmerk || !v.kenmerk.trim());
}

// ── Wat we bewust (nog) niet vragen ─────────────────────────────────────────────────────────────
//
// • Leeftijd van de bezoeker (mockup S02). Er is geen dierveld dat ermee correspondeert, dus het
//   antwoord kan de uitkomst niet beïnvloeden — dan is het alleen gegevensverzameling. Het
//   bouwplan §4 raadt leeftijdsprofilering zelf af.
// • Gewenst karakter (mockup S06) en de karakterchips op de resultaatkaart (S08). De `tags` op een
//   publicatie zijn op acceptatie én in de werkdatabase leeg; de vraag zou nergens tegenaan
//   scoren en de chips zouden verzonnen zijn.
// • Binnen-/buitenwens voor katten (bouwplan S03). De kenmerkencatalogus van de backoffice kent
//   hier geen vraag voor, dus er is aan de dierkant niets om mee te vergelijken. Dit kan er komen
//   zodra `KenmerkCatalogus` een sleutel als BINNEN_BUITEN krijgt.
// • De vrije wens (S06). Die hoort bij story 12-8 samen met de AI-vertaling naar chips; een
//   tekstvak dat nu al tekst opneemt waar niets mee gebeurt, doet alsof.
